import React from 'react';
import { ChatMessage } from '@azure/communication-chat';
import { CommunicationUserIdentifier } from '@azure/communication-common';
import {
  ChatMessageType,
  GuardianChatThreadResponse,
} from '@mnspro-cloud/services/lib/mnspro-cloud/api/v2';
import { GuardianChatMessage } from './GuardianChat.interface';

export function getInitials(name: string): string {
  if (!name) return '';

  return name
    .trim()
    .split(/\s+/) // split by one or more spaces
    .map((part) =>
      part
        .split('-') // handle hyphenated names
        .map((p) => p.charAt(0))
        .join(''),
    )
    .join('')
    .toUpperCase();
}

export const makeLinksClickable = (message: string) => {
  const urlRegex = /(https?:\/\/\S+)/g;

  return message.split(urlRegex).map((part, index) =>
    urlRegex.test(part) ? (
      <a href={part} target="_blank" rel="noopener noreferrer" key={index}>
        {part}
      </a>
    ) : (
      part
    ),
  );
};

/**
 * Maps the ACS message type onto the API message type.
 *
 * The ACS type names match the enum member names of {@link ChatMessageType},
 * unknown types fall back to `text`.
 */
const mapAcsMessageType = (type: ChatMessage['type']): ChatMessageType =>
  ChatMessageType[type as keyof typeof ChatMessageType] ?? ChatMessageType.text;

/**
 * Extracts the ACS user ID string from a ChatMessage sender.
 */
const extractSenderAcsUserIdFromChatMessage = (sender: ChatMessage['sender']): string => {
  if (!sender) {
    return '';
  }

  return (sender as CommunicationUserIdentifier).communicationUserId ?? '';
};

export const mapAcsChatMessageToGuardianChatMessage = (
  input: ChatMessage,
  thread: GuardianChatThreadResponse,
): GuardianChatMessage => {
  const acsUserId = extractSenderAcsUserIdFromChatMessage(input.sender);
  const participant = thread.participants.find((p) => p.acsUserId === acsUserId);

  const mapped = {
    id: input.id,
    content: input.content?.message ?? '',
    sender: {
      userId: participant?.userId,
      acsUserId: acsUserId,
      displayName: input.senderDisplayName ?? '',
      /** @type ChatUserType (teacher|parent/ward) */
      chatUserType: participant?.userType,
    },
    createdOn: input.createdOn.toISOString(),
    metadata: input.metadata ?? undefined,
    type: mapAcsMessageType(input.type),
  };

  return mapped;
};

const TWENTY_FOUR_HOURS_MS = 24 * 60 * 60 * 1000;

export const formatChatMessageDate = (createdOn?: string): string => {
  if (createdOn === undefined) {
    return '';
  }

  const date = new Date(createdOn);

  const isLessThanMinute = Date.now() - date.getTime() < 60 * 1000;

  if (isLessThanMinute) {
    return 'Jetzt'; // @todo Translate
  }

  const isOlderThan24h = Date.now() - date.getTime() > TWENTY_FOUR_HOURS_MS;

  if (isOlderThan24h) {
    return date.toLocaleString([], {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  }

  return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
};

export const isZeroGuid = (guid: string) => guid === '00000000-0000-0000-0000-000000000000';
